import { ReactNode } from 'react'
import { ArrowRight } from '@digital-twins/design-system/icons'
import { LibraryCollectionItem } from '@digital-twins/shared-types'

import {
  LIBRARY_CARD_CHIP_CLASS,
  LIBRARY_CARD_COVER_CHIP_CLASS,
  LIBRARY_CARD_DESCRIPTION_CLASS,
  LIBRARY_CARD_META_CLASS,
  LIBRARY_CARD_TITLE_CLASS,
} from '@/components/library/libraryCardStyles'
import { LibraryCoverImage, LibraryItemCover } from '@/components/library/LibraryItemCover'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import Link from '@/routing/Link'

/**
 * How a card sits in its section's grid: `hero` is a section's only item
 * (the full row, cover beside the text), `pair` one of two (half the row,
 * a larger title), `lead` the first of a section whose count leaves one
 * spare slot (two columns, cover beside the text), `standard` the rest.
 */
export type CollectionCardLayout = 'standard' | 'pair' | 'lead' | 'hero'

export interface CollectionItemCardProps {
  item: LibraryCollectionItem
  href: string
  /** Router state for the link (the Topic page reads its back label from it). */
  linkState?: unknown
  /** Every part's test id starts with it: `-title`, `-subtitle`, `-tint`. */
  testId: string
  /** Chips over the cover's bottom edge. */
  coverChips?: ReactNode
  /** Under the description (facet chips). */
  details?: ReactNode
  /** The dot separated meta row; the date goes last and never wraps. */
  meta?: ReactNode
  /** The card's place in its section's grid (`standard` by default). */
  layout?: CollectionCardLayout
  /** Overrides the cover image (a Topic draws its own image source). */
  imageUrl?: string | null
  /** Overrides the cover collage: an empty list keeps portraits off the cover (a study shows them as a stack). */
  portraits?: Array<string>
  /** A mixed collage that wins over portraits (a Topic's post images). */
  coverImages?: Array<LibraryCoverImage>
  /** The call to action a featured (`hero` or `lead`) card shows under its meta ("Chat with twins"). */
  cta?: string
  /** A smaller cover and title. Studies and audiences use this on the regular card. */
  compact?: boolean
  /** Drawn over the cover's top right, outside the card link (a remove control). */
  cornerAction?: ReactNode
  /**
   * A kind's own generated cover, drawn when the item has no image (a Topic's
   * platform marks). Rich enough to fill a featured slot at full width.
   */
  coverFallback?: ReactNode
}

interface LayoutClasses {
  root: string
  cover: string
  body: string
  title: string
  description: string
  meta: string
}

/** Classes per layout, written out whole so the class scanner sees them. */
const LAYOUT_CLASSES: Record<CollectionCardLayout, LayoutClasses> = {
  standard: { root: '', cover: 'aspect-[16/10]', body: '', title: '', description: '', meta: 'mt-auto' },
  pair: {
    root: '',
    cover: 'aspect-[16/10] @[860px]/collection:aspect-[16/9]',
    body: '@[560px]/collection:gap-2.5 @[560px]/collection:pt-4',
    // Card titles stay under the 36 px section heading: a pair reads one step up from a standard card.
    title: '@[560px]/collection:text-[22px] @[560px]/collection:leading-[1.2]',
    description: '@[560px]/collection:text-[15px]',
    meta: 'mt-auto',
  },
  lead: {
    root: '@[1160px]/collection:col-span-2 @[1160px]/collection:flex-row @[1160px]/collection:items-stretch @[1160px]/collection:gap-6',
    cover:
      'aspect-[16/10] @[1160px]/collection:aspect-auto @[1160px]/collection:min-h-[240px] @[1160px]/collection:w-[58%] @[1160px]/collection:self-stretch',
    // Beside the cover the text sits on a soft tinted panel as tall as the row, so it never floats on the page.
    body: '@[1160px]/collection:justify-center @[1160px]/collection:gap-3 @[1160px]/collection:rounded-[14px] @[1160px]/collection:bg-primary/[0.045] @[1160px]/collection:px-7 @[1160px]/collection:py-6 @[1160px]/collection:transition-colors @[1160px]/collection:group-hover:bg-primary/[0.08]',
    // Large featured titles go back to the regular weight; the padding keeps descenders inside the clamp.
    title:
      '@[1160px]/collection:line-clamp-3 @[1160px]/collection:pb-[0.12em] @[1160px]/collection:text-[28px] @[1160px]/collection:font-normal @[1160px]/collection:leading-[1.15]',
    description: '@[1160px]/collection:line-clamp-4 @[1160px]/collection:text-[15px]',
    meta: 'mt-auto @[1160px]/collection:mt-1',
  },
  hero: {
    root: 'col-span-full @[760px]/collection:flex-row @[760px]/collection:items-stretch @[760px]/collection:gap-4',
    cover:
      'aspect-[16/10] @[760px]/collection:aspect-[16/10] @[760px]/collection:w-[55%] @[760px]/collection:self-start',
    // The text panel matches the cover's height: a tinted surface, rounded like the cover.
    body: '@[760px]/collection:justify-center @[760px]/collection:gap-3.5 @[760px]/collection:rounded-[14px] @[760px]/collection:bg-primary/[0.045] @[760px]/collection:px-10 @[760px]/collection:py-8 @[760px]/collection:transition-colors @[760px]/collection:group-hover:bg-primary/[0.08]',
    title:
      '@[760px]/collection:line-clamp-3 @[760px]/collection:pb-[0.12em] @[760px]/collection:text-[30px] @[760px]/collection:font-normal @[760px]/collection:leading-[1.15] @[1160px]/collection:text-[32px]',
    description: '@[760px]/collection:line-clamp-5 @[760px]/collection:text-[16px] @[760px]/collection:leading-[1.55]',
    // Title, description, meta and the call to action center as one block: no gap between them.
    meta: 'mt-auto @[760px]/collection:mt-1',
  },
}

/**
 * The hero's cover when the item has no image: a shorter, deeper generated
 * cover, so a featured slot never reads as a giant pale placeholder.
 */
const HERO_WITHOUT_IMAGE_COVER_CLASS =
  'aspect-[16/10] @[760px]/collection:aspect-[4/3] @[760px]/collection:w-[38%] @[760px]/collection:max-w-[400px] @[760px]/collection:self-center'

/** A featured card's call to action: a pill drawn inside the card link (never a nested link). */
const CTA_CLASS =
  'mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground shadow-sm transition-shadow group-hover:shadow-md'

const COMPACT_COVER_CLASS = 'aspect-[2/1]'

const COMPACT_TITLE_CLASS =
  'font-display text-[16px] font-bold [:root:not([data-heading-font])_&]:font-normal leading-[1.28] tracking-[-0.005em] text-foreground line-clamp-2 pb-[0.1em]'

const COMPACT_DESCRIPTION_CLASS = 'text-[13px] leading-[1.45] text-muted-foreground line-clamp-2'

export interface CoverChipProps {
  children: ReactNode
  testId?: string
  className?: string
}

/** A chip over a cover, in the dark glass tone. */
export function CoverChip(props: CoverChipProps) {
  const { children, testId, className } = props

  return (
    <Badge
      variant="muted"
      className={cn(LIBRARY_CARD_CHIP_CLASS, LIBRARY_CARD_COVER_CHIP_CLASS, 'min-w-0 shrink-0 gap-1', className)}
      data-testid={testId}
    >
      {children}
    </Badge>
  )
}

/**
 * A collection item in the Library's magazine language: the cover carries
 * the frame (photos, twin portraits or a generated token cover), then the
 * display title, a two line description and the meta row. The whole card is
 * one link.
 */
export function CollectionItemCard(props: CollectionItemCardProps) {
  const {
    item,
    href,
    linkState,
    testId,
    coverChips,
    details,
    meta,
    layout = 'standard',
    imageUrl,
    portraits,
    coverImages,
    cta,
    coverFallback,
    compact = false,
    cornerAction,
  } = props

  const classes = LAYOUT_CLASSES[layout]
  const isCompact = compact && layout === 'standard'
  const resolvedImage = imageUrl === undefined ? item.imageUrl : imageUrl
  const resolvedPortraits = portraits === undefined ? item.portraits : portraits
  const hasImage =
    Boolean(resolvedImage) || (resolvedPortraits ?? []).length > 0 || (coverImages ?? []).length > 0
  const isFeatured = layout === 'hero' || layout === 'lead'
  const isBareHero = layout === 'hero' && !hasImage && !coverFallback
  const coverClass = isBareHero ? HERO_WITHOUT_IMAGE_COVER_CLASS : isCompact ? COMPACT_COVER_CLASS : classes.cover

  const card = (
    <Link
      href={href}
      state={linkState}
      className={cn(
        'group flex h-full min-w-0 flex-col rounded-[14px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        cornerAction ? 'relative' : classes.root,
      )}
      data-testid={testId}
      data-kind={item.kind}
      data-layout={layout}
      data-featured={layout === 'standard' ? undefined : 'true'}
      data-has-image={hasImage ? 'true' : 'false'}
    >
      <LibraryItemCover
        kind={item.kind}
        seed={item.token}
        imageUrl={resolvedImage}
        portraits={resolvedPortraits}
        images={coverImages && coverImages.length >= 2 ? coverImages : undefined}
        overlay={coverChips}
        testId={testId}
        tone={isFeatured ? 'bold' : 'soft'}
        fallback={coverFallback}
        className={cn('shrink-0', coverClass)}
      />
      <div className={cn('flex min-w-0 flex-1 flex-col gap-1.5 pt-2.5', !isCompact && 'gap-2 pt-3', classes.body)}>
        <h3
          className={cn(isCompact ? COMPACT_TITLE_CLASS : LIBRARY_CARD_TITLE_CLASS, classes.title)}
          data-testid={`${testId}-title`}
        >
          {item.title}
        </h3>
        {item.subtitle ? (
          <p
            className={cn(isCompact ? COMPACT_DESCRIPTION_CLASS : LIBRARY_CARD_DESCRIPTION_CLASS, classes.description)}
            data-testid={`${testId}-subtitle`}
          >
            {item.subtitle}
          </p>
        ) : null}
        {details}
        {meta ? (
          <div className={cn(LIBRARY_CARD_META_CLASS, 'pt-1', classes.meta)} data-testid={`${testId}-meta`}>
            {meta}
          </div>
        ) : null}
        {cta && isFeatured ? (
          <span
            className={cn(CTA_CLASS, layout === 'lead' ? 'hidden @[1160px]/collection:inline-flex' : null)}
            data-testid={`${testId}-cta`}
          >
            {cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </span>
        ) : null}
      </div>
    </Link>
  )

  if (!cornerAction) {
    return card
  }

  return (
    <div className={cn('relative h-full min-w-0', classes.root)}>
      {card}
      <div className="absolute right-2 top-2 z-10">{cornerAction}</div>
    </div>
  )
}

export interface MetaValueProps {
  children: ReactNode
  testId?: string
}

/** A meta row value that never breaks (dates, counts). */
export function MetaValue(props: MetaValueProps) {
  const { children, testId } = props

  return (
    <span className="whitespace-nowrap" data-testid={testId}>
      {children}
    </span>
  )
}

/** The dot between meta values. */
export function MetaDot() {
  return <span aria-hidden="true">·</span>
}
